import * as Sentry from '@sentry/browser';
import { ReportingService } from './service';
import { env } from '$lib/config/environment';
import { Log } from '@ainova/core';

/**
 * Sentry adapter for the reporting service.
 */
export class ReportingServiceSentry extends ReportingService {
  private logger = new Log('Sentry');
  private initialized = false;

  constructor() {
    super();

    if (env.sentry.enabled && env.sentry.dsn) {
      try {
        Sentry.init({
          dsn: env.sentry.dsn,
          environment: env.sentry.environment,
          tracesSampleRate: env.features.debugging ? 1.0 : 0.1,
          beforeSend: (event, hint) => {
            // Filter out certain errors in production
            if (!env.features.debugging) {
              // Don't send network errors in production
              if (
                hint.originalException instanceof Error &&
                hint.originalException.name === 'NetworkError'
              ) {
                return null;
              }
            }
            return event;
          }
        });

        this.initialized = true;
        this.logger.info('Sentry initialized successfully');
      } catch (error) {
        this.logger.error('Failed to initialize Sentry', error);
      }
    } else {
      this.logger.info('Sentry is disabled');
    }
  }

  async error(message: string, context?: Record<string, unknown>): Promise<void> {
    if (!this.initialized) {
      return;
    }

    try {
      if (context instanceof Error) {
        Sentry.captureException(context, {
          extra: { message }
        });
      } else {
        Sentry.captureMessage(message, 'error');
        if (context) {
          Sentry.setContext('error_context', context);
        }
      }
    } catch (err) {
      this.logger.error('Failed to report error to Sentry', err);
    }
  }

  async warning(message: string, context?: Record<string, unknown>): Promise<void> {
    if (!this.initialized) {
      return;
    }

    try {
      Sentry.captureMessage(message, 'warning');
      if (context) {
        Sentry.setContext('warning_context', context);
      }
    } catch (err) {
      this.logger.error('Failed to report warning to Sentry', err);
    }
  }

  async info(message: string, context?: Record<string, unknown>): Promise<void> {
    if (!this.initialized) {
      return;
    }

    try {
      Sentry.captureMessage(message, 'info');
      if (context) {
        Sentry.setContext('info_context', context);
      }
    } catch (err) {
      this.logger.error('Failed to report info to Sentry', err);
    }
  }
}
